import { getAuth } from 'firebase/auth'
import { useCallback } from 'preact/hooks'
import { navigate } from 'wouter-preact/use-browser-location'
import { useSignal } from '@preact/signals'
import { getToken } from 'firebase/app-check'

import { Button, Input, Typography } from 'components'
import { application, getPlans, getUserInformation } from 'state'
import { firebaseAppCheckState } from 'state/auth/state'

import { getOnboardingQuestions } from '../onboarding_form/state'
import {
	firstNameState,
	lastNameState,
	usernameState,
	formErrors,
	setUsername,
	setFirstName,
	setLastName
} from './state'

const ProfileForm = ({
	navigateToOnboardingPage
}: {
	navigateToOnboardingPage: () => void
}) => {
	const isUpdatingProfile = useSignal(false)

	const handleSubmit = useCallback(async (e: Event) => {
		e.preventDefault()

		isUpdatingProfile.value = true

		try {
			let token = await getAuth().currentUser?.getIdToken()
			const appCheckToken = await getToken(firebaseAppCheckState.peek()!)

			if (!appCheckToken.token) {
				console.error('App check token is not available')
				navigate('/', { replace: true })
			}

			if (!token) {
				navigate('/', { replace: true })
				return
			}

			await application.peek().forManagingAccount().signUp({
				first_name: firstNameState.peek(),
				last_name: lastNameState.peek(),
				id_token: token,
				handler: usernameState.peek()
			})

			if (usernameState.peek()) {
				// refresh user credentials to get the custom claims like id, first name, etc.
				token = await getAuth().currentUser?.getIdToken(true)
				if (!token) {
					navigate('/', { replace: true })
					console.error('Error getting token')
					return
				}
				await application
					.peek()
					.forManagingAccount()
					.signIn(token, appCheckToken.token)

				await getUserInformation()

				await Promise.all([getOnboardingQuestions(), getPlans()])

				navigateToOnboardingPage()
			}
		} catch (error) {
			console.error('Error updating profile:', error)
		} finally {
			isUpdatingProfile.value = false
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return (
		<div class='w-[30rem] rounded-lg border border-black-600 p-8 pb-12'>
			<Typography
				class='text-center'
				color='text-product'
				text='Complete your profile'
				variant='h2'
			/>
			<div class='mb-8 mt-6 text-center'>
				<Typography
					color='text-white'
					text='Please fill in the following fields to complete your profile.'
					variant='text2'
				/>
			</div>

			<form class='w-full space-y-7' onSubmit={handleSubmit}>
				<Input
					error={formErrors.value?.username}
					hasError={!!formErrors.value?.username}
					id='username'
					label='Username *'
					name='username'
					onInput={setUsername}
					placeholder='Username'
					value={usernameState}
					fullWidth
				/>
				<Input
					error={formErrors.value?.firstName}
					hasError={!!formErrors.value?.firstName}
					id='firstName'
					label='First Name'
					name='firstName'
					onInput={setFirstName}
					placeholder='John'
					value={firstNameState}
					fullWidth
				/>
				<Input
					error={formErrors.value?.lastName}
					hasError={!!formErrors.value?.lastName}
					id='lastName'
					label='Last Name'
					name='lastName'
					onInput={setLastName}
					placeholder='Doe'
					value={lastNameState}
					fullWidth
				/>
				<div>
					<Button
						className='mt-10'
						disabled={
							isUpdatingProfile.value ||
							usernameState.value === '' ||
							!!formErrors.value.firstName ||
							!!formErrors.value.lastName ||
							!!formErrors.value.username
						}
						isLoading={isUpdatingProfile.value}
						text='Complete'
						type='submit'
						fullWidth
					/>
				</div>
			</form>
		</div>
	)
}

export { ProfileForm }
